import { reactive } from 'vue';
import { CARD_LIMITS, CardLookSchema, CardStateSchema, type CardLook, type CardState } from '@vp/shared-types';
import { profileLocal, profileScope } from './profiles/local';

/**
 * Profile cards 2.0 (module API 4.10), the profile's side:
 * - looks: what the person chose for a card (or the tray) in "Edit" — over the module's look;
 * - state: the values of a card's interactive elements (`data-vp-toggle`, `-counter`, `-note`), kept
 *   by the platform so a card shows them without its module's thread.
 * Both are keys of the profile (`profileLocal`), ride along in `.vpprofile` and keep `sync` / `rev`
 * for a future sync, like the tray layout.
 */

export const PROFILE_LOOKS_KEY = 'vp-profile-looks';
export const PROFILE_CARD_STATE_KEY = 'vp-profile-card-state';

interface LookSheet {
  v: 1;
  sync: 'pending' | 'acked';
  rev: number;
  cards: Record<string, CardLook>;
  tray?: CardLook;
}

interface StateSheet {
  v: 1;
  sync: 'pending' | 'acked';
  rev: number;
  cards: Record<string, CardState>;
}

function read<T>(key: string): T | null {
  try {
    return JSON.parse(profileLocal.getItem(key) ?? 'null') as T | null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    profileLocal.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked: this visit still shows it */
  }
}

function loadLooks(): LookSheet {
  const raw = read<Partial<LookSheet>>(PROFILE_LOOKS_KEY);
  const cards: Record<string, CardLook> = {};
  for (const [id, look] of Object.entries(raw?.cards ?? {})) {
    const ok = CardLookSchema.safeParse(look);
    if (ok.success) cards[id] = ok.data;
  }
  const tray = CardLookSchema.safeParse(raw?.tray);
  return { v: 1, sync: raw?.sync === 'pending' ? 'pending' : 'acked', rev: typeof raw?.rev === 'number' ? raw.rev : 0, cards, ...(raw?.tray && tray.success ? { tray: tray.data } : {}) };
}

function loadState(): StateSheet {
  const raw = read<Partial<StateSheet>>(PROFILE_CARD_STATE_KEY);
  const cards: Record<string, CardState> = {};
  for (const [id, values] of Object.entries(raw?.cards ?? {})) {
    const ok = CardStateSchema.safeParse(values);
    if (ok.success) cards[id] = ok.data;
  }
  return { v: 1, sync: raw?.sync === 'pending' ? 'pending' : 'acked', rev: typeof raw?.rev === 'number' ? raw.rev : 0, cards };
}

const sheets = reactive({ looks: loadLooks(), state: loadState(), scope: profileScope() });

/** The keys belong to the profile: after a switch they are read again. */
function fresh(): typeof sheets {
  if (sheets.scope !== profileScope()) {
    sheets.looks = loadLooks();
    sheets.state = loadState();
    sheets.scope = profileScope();
  }
  return sheets;
}

/** The person's looks (reactive). */
export function profileLooks(): LookSheet {
  return fresh().looks;
}

const plain = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

function saveLooks(): void {
  const l = fresh().looks;
  l.sync = 'pending';
  l.rev++;
  write(PROFILE_LOOKS_KEY, plain(l));
}

function saveState(): void {
  const st = fresh().state;
  st.sync = 'pending';
  st.rev++;
  write(PROFILE_CARD_STATE_KEY, plain(st));
}

/** The person's look of a card (`id` of the panel) or of the tray (`null`); `null` look — back to the module's. */
export function setPersonLook(id: string | null, look: CardLook | null): void {
  const ok = look ? CardLookSchema.safeParse(look) : null;
  if (ok && !ok.success) return;
  const l = fresh().looks;
  if (id === null) {
    if (ok) l.tray = ok.data;
    else delete l.tray;
  } else if (ok) l.cards[id] = ok.data;
  else delete l.cards[id];
  saveLooks();
}

/** The look a card is drawn with: the module's, with the person's choices over it. */
export function cardLook(id: string, moduleLook?: CardLook): CardLook {
  return { ...(moduleLook ?? {}), ...(fresh().looks.cards[id] ?? {}) };
}

export function cardState(id: string): CardState {
  return fresh().state.cards[id] ?? {};
}

/** Merge values into a card's state (at most CARD_LIMITS.stateKeys keys). */
export function mergeCardState(id: string, values: CardState): boolean {
  const next = { ...cardState(id), ...values };
  if (!CardStateSchema.safeParse(next).success || Object.keys(next).length > CARD_LIMITS.stateKeys) return false;
  fresh().state.cards[id] = next;
  saveState();
  return true;
}

/** A card was removed with its module: its state and the person's look go too. */
export function forgetCard(id: string): void {
  const s = fresh();
  if (s.state.cards[id]) {
    delete s.state.cards[id];
    saveState();
  }
  if (s.looks.cards[id]) {
    delete s.looks.cards[id];
    saveLooks();
  }
}
